import { useState, useEffect, useCallback } from 'react'
import { useEngineManager } from './useWorkspace'
import type { McpConnectionEntry, WorkspaceEntry } from '../engine/types'
import { buildNewConnection, buildUpdatedConfig, generateConfigConnectionToken, metadataFromConnection } from '../engine/mcp/client'
import { generateMcpSnippet, getConfigReaderSeed } from '../engine/mcp/crypto'
import { createConfigBlob, readConfigBlob, updateConfigBlob } from '../engine/mcp/configBlobService'
import { requiresConnectionRecreation } from '../engine/mcp/connectionEditing'
import type { McpConfig, McpConfigOwner, McpWorkspaceConfig } from '../engine/mcp/types'
import { grantMcpWorkspaceAccess, revokeMcpWorkspaceAccess, softDeleteMcpConfigBlob } from '../engine/mcp/accessControl'
import { decodeWorkspaceSecretV2, type WorkspaceSecretV2 } from '@secureblob/sdk/workspaceSecretV2'

/**
 * Hook for managing MCP connections via the EngineManager registry.
 * Connections are stored in _registry~{kid}.mcpConnections[] (spec §07 §3.4).
 *
 * The `create` function performs the full flow:
 *   1. Build connection data structures (pure function)
 *   2. Create config SecureBlob in CryptoBlobStore (HTTP API)
 *   3. Add registry entry via EngineManager
 *
 * The `edit` function updates an existing connection:
 *   1. Build updated config (pure function)
 *   2. Update config SecureBlob in CryptoBlobStore (non-genesis commit)
 *   3. Update registry entry via EngineManager
 */
export function useMcpConnections() {
    const engine = useEngineManager()
    const [connections, setConnections] = useState<McpConnectionEntry[]>([])

    const resolveApiBaseUrl = useCallback((entry: Pick<McpConnectionEntry, 'workspaceBlobIds'>) => {
        const registry = engine.getRegistry()
        return entry.workspaceBlobIds
            .map(blobId => registry.find((w: WorkspaceEntry) => w.blobId === blobId)?.apiBaseUrl)
            .find((candidate): candidate is string => typeof candidate === 'string' && candidate.length > 0)
            ?? registry[0]?.apiBaseUrl
            ?? 'https://secureblob.hoglundit.se/api/cryptoblobstore'
    }, [engine])

    const refresh = useCallback(() => {
        const list = engine.getMcpConnections()
        list.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        setConnections(list)
    }, [engine])

    // Refresh on mount + registry changes
    useEffect(() => {
        refresh()
        return engine.on('registry:changed', () => refresh())
    }, [engine, refresh])

    const create = useCallback(async (
        label: string,
        workspaces: McpWorkspaceConfig[],
        mcpReaderSeeds?: Record<string, string>,
        mcpWriterSeeds?: Record<string, string>,
    ) => {
        // Identity is required — owner is a mandatory field (spec §70 §6.2)
        const identity = engine.getIdentity()
        if (!identity) throw new Error('Cannot create MCP connection without an active identity')
        const owner: McpConfigOwner = { primaryKid: identity.kid, displayName: identity.name, emoji: identity.emoji }

        // Determine API base URL (default to first workspace's API)
        const apiBaseUrl = engine.getRegistry().find((w: WorkspaceEntry) => w.blobId === workspaces[0]?.blobId)?.apiBaseUrl
            ?? engine.getRegistry()[0]?.apiBaseUrl // fallback

        const { entry, token, config } = buildNewConnection(label, workspaces, owner, mcpReaderSeeds, mcpWriterSeeds, apiBaseUrl)
        const registryEntries = engine.getRegistry()
        const grantedBlobIds: string[] = []

        const getManagerSecret = (blobId: string): WorkspaceSecretV2 | null => {
            const registryEntry = registryEntries.find((candidate: WorkspaceEntry) => candidate.blobId === blobId)
            const secret = registryEntry ? decodeWorkspaceSecretV2(registryEntry.capability) : null
            return secret?.writerSeed ? secret : null
        }

        try {
            for (const workspace of workspaces) {
                const managerSecret = getManagerSecret(workspace.blobId)
                if (!managerSecret) {
                    throw new Error(`Workspace '${workspace.title}' requires writer/manage access to issue an MCP credential.`)
                }

                await grantMcpWorkspaceAccess(
                    apiBaseUrl,
                    managerSecret,
                    mcpReaderSeeds?.[workspace.blobId] ?? managerSecret.readerSeed,
                    mcpWriterSeeds?.[workspace.blobId],
                )
                grantedBlobIds.push(workspace.blobId)
            }

            // Create the config SecureBlob in CryptoBlobStore (spec §70 §6.1)
            await createConfigBlob(
                apiBaseUrl,
                entry.configBlobId,
                entry.readKey,
                entry.readerSeed!,
                entry.writerSeed,
                config,
            )

            // Register the connection in the user's registry
            engine.addMcpConnection(entry)
            refresh()

            return { entry, token }
        } catch (error) {
            for (const blobId of grantedBlobIds) {
                try {
                    const managerSecret = getManagerSecret(blobId)
                    if (!managerSecret) continue
                    await revokeMcpWorkspaceAccess(
                        apiBaseUrl,
                        managerSecret,
                        mcpReaderSeeds?.[blobId],
                        mcpWriterSeeds?.[blobId],
                    )
                } catch {
                    // Best-effort rollback only.
                }
            }
            throw error
        }
    }, [engine, refresh])

    const edit = useCallback(async (
        configBlobId: string,
        label: string,
        workspaces: McpWorkspaceConfig[],
        mcpReaderSeeds?: Record<string, string>,
        mcpWriterSeeds?: Record<string, string>,
    ) => {
        // Find existing connection for keys
        const existing = connections.find(c => c.configBlobId === configBlobId)
        if (!existing) throw new Error(`Connection ${configBlobId} not found`)
        if (requiresConnectionRecreation(existing)) {
            throw new Error('This MCP connection predates auth-v2 revoke semantics and must be recreated.')
        }

        // Identity is required — owner is a mandatory field (spec §70 §6.2)
        const identity = engine.getIdentity()
        if (!identity) throw new Error('Cannot update MCP connection without an active identity')
        const owner: McpConfigOwner = { primaryKid: identity.kid, displayName: identity.name, emoji: identity.emoji }
        const config = buildUpdatedConfig(label, workspaces, owner, metadataFromConnection(existing))

        // Determine API base URL
        const apiBaseUrl = engine.getRegistry().find((w: WorkspaceEntry) => w.blobId === workspaces[0]?.blobId)?.apiBaseUrl
            ?? engine.getRegistry()[0]?.apiBaseUrl
        const registryEntries = engine.getRegistry()
        const nextWorkspaceIds = new Set(workspaces.map(w => w.blobId))
        const previousWorkspaceIds = new Set(existing.workspaceBlobIds)
        const grantedNow: Array<{ blobId: string; readerSeed?: string; writerSeed?: string }> = []

        const getManagerSecret = (blobId: string): WorkspaceSecretV2 | null => {
            const registryEntry = registryEntries.find((candidate: WorkspaceEntry) => candidate.blobId === blobId)
            const secret = registryEntry ? decodeWorkspaceSecretV2(registryEntry.capability) : null
            return secret?.writerSeed ? secret : null
        }

        try {
            for (const workspace of workspaces) {
                const managerSecret = getManagerSecret(workspace.blobId)
                if (!managerSecret) {
                    throw new Error(`Workspace '${workspace.title}' requires writer/manage access to issue or rotate MCP credentials.`)
                }

                const nextReaderSeed = mcpReaderSeeds?.[workspace.blobId]
                const previousReaderSeed = existing.mcpReaderSeeds?.[workspace.blobId]
                const nextWriterSeed = mcpWriterSeeds?.[workspace.blobId]
                const previousWriterSeed = existing.mcpWriterSeeds?.[workspace.blobId]

                const readerChanged = nextReaderSeed && nextReaderSeed !== previousReaderSeed
                const writerAdded = !!nextWriterSeed && nextWriterSeed !== previousWriterSeed

                if ((readerChanged || writerAdded) || !previousWorkspaceIds.has(workspace.blobId)) {
                    await grantMcpWorkspaceAccess(
                        apiBaseUrl,
                        managerSecret,
                        nextReaderSeed ?? managerSecret.readerSeed,
                        nextWriterSeed,
                    )
                    grantedNow.push({ blobId: workspace.blobId, readerSeed: nextReaderSeed, writerSeed: nextWriterSeed })
                }
            }

            // Update the config SecureBlob (non-genesis commit, spec §70 §3.3)
            await updateConfigBlob(
                apiBaseUrl,
                existing.configBlobId,
                existing.readKey,
                getConfigReaderSeed(existing),
                existing.writerSeed,
                config,
            )

            for (const blobId of new Set([...existing.workspaceBlobIds, ...Array.from(nextWorkspaceIds)])) {
                const managerSecret = getManagerSecret(blobId)
                if (!managerSecret) continue

                const shouldRemoveWorkspace = !nextWorkspaceIds.has(blobId)
                const previousReaderSeed = existing.mcpReaderSeeds?.[blobId]
                const previousWriterSeed = existing.mcpWriterSeeds?.[blobId]
                const nextReaderSeed = mcpReaderSeeds?.[blobId]
                const nextWriterSeed = mcpWriterSeeds?.[blobId]

                if (shouldRemoveWorkspace) {
                    await revokeMcpWorkspaceAccess(apiBaseUrl, managerSecret, previousReaderSeed, previousWriterSeed)
                    continue
                }

                if (previousReaderSeed && nextReaderSeed && previousReaderSeed !== nextReaderSeed) {
                    await revokeMcpWorkspaceAccess(apiBaseUrl, managerSecret, previousReaderSeed)
                }

                if (previousWriterSeed && previousWriterSeed !== nextWriterSeed) {
                    await revokeMcpWorkspaceAccess(apiBaseUrl, managerSecret, undefined, previousWriterSeed)
                }
            }

            // Update registry entry (include MCP seeds for deterministic rebuild/revoke)
            engine.updateMcpConnection(configBlobId, {
                label,
                workspaceBlobIds: workspaces.map(w => w.blobId),
                ...(mcpReaderSeeds ? { mcpReaderSeeds } : {}),
                ...(mcpWriterSeeds ? { mcpWriterSeeds } : {}),
                ...(existing.purpose ? { purpose: existing.purpose } : {}),
                ...(existing.parentConfigBlobId ? { parentConfigBlobId: existing.parentConfigBlobId } : {}),
                ...(existing.linkedConfigBlobIds?.length ? { linkedConfigBlobIds: existing.linkedConfigBlobIds } : {}),
                ...(existing.linkedWorkspaceRefs?.length ? { linkedWorkspaceRefs: existing.linkedWorkspaceRefs } : {}),
            })
            refresh()
        } catch (error) {
            for (const granted of grantedNow) {
                try {
                    const managerSecret = getManagerSecret(granted.blobId)
                    if (!managerSecret) continue
                    await revokeMcpWorkspaceAccess(apiBaseUrl, managerSecret, granted.readerSeed, granted.writerSeed)
                } catch {
                    // Best-effort rollback only.
                }
            }
            throw error
        }
    }, [engine, connections, refresh])

    const remove = useCallback(async (configBlobId: string) => {
        const existing = connections.find(c => c.configBlobId === configBlobId)
        if (!existing) return

        const registryEntries = engine.getRegistry()
        const apiBaseUrl = registryEntries.find((w: WorkspaceEntry) => w.blobId === existing.workspaceBlobIds[0])?.apiBaseUrl
            ?? registryEntries[0]?.apiBaseUrl
        if (!apiBaseUrl) throw new Error('No API base URL available for MCP revoke.')

        await softDeleteMcpConfigBlob(apiBaseUrl, existing)

        for (const blobId of existing.workspaceBlobIds) {
            const registryEntry = registryEntries.find((candidate: WorkspaceEntry) => candidate.blobId === blobId)
            const managerSecret = registryEntry ? decodeWorkspaceSecretV2(registryEntry.capability) : null
            if (!managerSecret?.writerSeed) continue
            await revokeMcpWorkspaceAccess(
                apiBaseUrl,
                managerSecret,
                existing.mcpReaderSeeds?.[blobId],
                existing.mcpWriterSeeds?.[blobId],
            )
        }

        engine.removeMcpConnection(configBlobId)
        refresh()
    }, [connections, engine, refresh])

    const update = useCallback((configBlobId: string, updates: Partial<McpConnectionEntry>) => {
        engine.updateMcpConnection(configBlobId, updates)
        refresh()
    }, [engine, refresh])

    const readLiveConfig = useCallback(async (entry: McpConnectionEntry): Promise<McpConfig> => {
        return readConfigBlob(
            resolveApiBaseUrl(entry),
            entry.configBlobId,
            entry.readKey,
            getConfigReaderSeed(entry),
            entry.writerSeed,
        )
    }, [resolveApiBaseUrl])

    const importWorkspaceFromConfig = useCallback((
        connection: McpConnectionEntry,
        workspace: McpWorkspaceConfig,
    ) => {
        const decoded = decodeWorkspaceSecretV2(workspace.workspaceSecret)
        engine.addToRegistry({
            blobId: workspace.blobId,
            label: workspace.title || workspace.alias || workspace.blobId,
            emoji: workspace.emoji ?? 'db',
            apiBaseUrl: resolveApiBaseUrl(connection),
            capability: workspace.workspaceSecret,
            trustLevel: 'trusted',
            canWrite: Boolean(decoded?.writerSeed && workspace.permissions.write),
        })
        refresh()
    }, [engine, refresh, resolveApiBaseUrl])

    const getToken = useCallback((entry: McpConnectionEntry) => {
        return generateConfigConnectionToken(entry, resolveApiBaseUrl(entry))
    }, [resolveApiBaseUrl])

    const getSnippet = useCallback((entry: McpConnectionEntry, client?: 'cursor' | 'antigravity' | 'header') => {
        return generateMcpSnippet(entry, client, undefined, client === 'header' ? getToken(entry) : undefined)
    }, [getToken])

    return { connections, create, edit, remove, update, readLiveConfig, importWorkspaceFromConfig, getToken, getSnippet }
}
